import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import {
  SESSION_TOKEN_KEY,
  checkPendingInvites,
  fetchUserStatus,
  recordMauOncePerDay,
  type UserStatus,
} from '@/hooks/auth/utils';
import { useSingleSessionPoll } from '@/hooks/auth/useSingleSessionPoll';
import { useUserProfile } from '@/hooks/auth/useUserProfile';
import { useIdleLogout } from '@/hooks/auth/useIdleLogout';
import { useSessionRevalidate } from '@/hooks/auth/useSessionRevalidate';

interface AuthStateValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userStatus: UserStatus;
}

interface AuthActionsValue {
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phone?: string,
    captchaToken?: string,
  ) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

type AuthContextType = AuthStateValue & AuthActionsValue;

// Dois contexts internos: estado (muda no login/logout/boot) e ações (estáveis).
// Componentes que só consomem ações deixam de re-renderizar quando o estado muda.
const AuthStateContext = createContext<AuthStateValue | undefined>(undefined);
const AuthActionsContext = createContext<AuthActionsValue | undefined>(undefined);


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const isSigningOut = useRef(false);

  // Idle logout (45 min) + revalidação periódica do JWT (5 min)
  useIdleLogout({ minutes: 45 });
  useSessionRevalidate({ minutes: 5 });

  // Status do perfil (lazy / assíncrono)
  const { status: userStatus, setStatus: setUserStatus } = useUserProfile(user?.id ?? null);

  // Watcher de sessão única (realtime + polling + foco)
  const sessionWatch = useSingleSessionPoll({
    onMismatch: async (message) => {
      if (isSigningOut.current) return;
      isSigningOut.current = true;
      sessionWatch.stop();
      localStorage.removeItem(SESSION_TOKEN_KEY);
      setUserStatus(null);
      await supabase.auth.signOut({ scope: 'global' });
      isSigningOut.current = false;
      if (message) toast.warning(message, { duration: 10000 });
    },
  });

  useEffect(() => {
    let initialLoad = true;
    let prevUserId: string | null = null;
    let prevAccessToken: string | null = null;

    const applySession = (newSession: Session | null) => {
      const newUserId = newSession?.user?.id ?? null;
      const newToken = newSession?.access_token ?? null;
      const userChanged = newUserId !== prevUserId;
      const tokenChanged = newToken !== prevAccessToken;

      if (userChanged) {
        setUser(newSession?.user ?? null);
        prevUserId = newUserId;
      }
      if (tokenChanged) {
        setSession(newSession);
        prevAccessToken = newToken;
      }
      return userChanged;
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      const userChanged = applySession(s);
      if (initialLoad) {
        initialLoad = false;
        setLoading(false);
      }
      if (s?.user) {
        if (userChanged) {
          recordMauOncePerDay();
          const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
          if (storedToken) sessionWatch.start(s.user.id);
        }
      } else if (userChanged) {
        sessionWatch.stop();
      }
    });

    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        if (initialLoad) {
          applySession(s);
          initialLoad = false;
          setLoading(false);
          if (s?.user) {
            recordMauOncePerDay();
            const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
            if (storedToken) sessionWatch.start(s.user.id);
          }
        }
      })
      .catch((err) => {
        log.error({ err: { message: (err as Error)?.message } }, 'auth.getSession.failed');
        if (initialLoad) {
          initialLoad = false;
          setLoading(false);
        }
      });

    const timeout = setTimeout(() => {
      if (initialLoad) {
        initialLoad = false;
        setLoading(false);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
      sessionWatch.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phone?: string,
    captchaToken?: string,
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        captchaToken,
        data: { first_name: firstName, last_name: lastName, phone },
      },
    });

    if (!error && phone && data.user?.id) {
      try {
        await supabase.from('profiles').update({ phone }).eq('id', data.user.id);
      } catch (err) {
        const { log } = await import('@/lib/logger');
        log.warn({ err, userId: data.user.id }, 'auth.signup.phone_update_failed');
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const deviceInfo = `${navigator.userAgent.substring(0, 100)}`;
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/enforce-single-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            email,
            password,
            device_info: deviceInfo,
            captcha_token: captchaToken,
          }),
        },
      );

      const result = await response.json();
      if (!response.ok || !result.session) {
        return { error: new Error(result.error || 'Erro ao fazer login') };
      }

      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setSessionError) return { error: setSessionError };

      localStorage.setItem(SESSION_TOKEN_KEY, result.session_token);

      if (result.session.user) {
        const status = await fetchUserStatus(result.session.user.id);
        setUserStatus(status);
        sessionWatch.start(result.session.user.id);
        setTimeout(() => checkPendingInvites(result.session.user.email), 1000);
      }

      return { error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      return { error: err instanceof Error ? err : new Error('Erro ao fazer login') };
    }
  };

  const signOut = async () => {
    sessionWatch.stop();
    localStorage.removeItem(SESSION_TOKEN_KEY);
    setUserStatus(null);
    await supabase.auth.signOut({ scope: 'global' });
  };

  // Refs garantem que o objeto de ações exposto seja estável (criado uma vez),
  // sem prender closures velhas das funções signUp/signIn/signOut.
  const signUpRef = useRef(signUp);
  const signInRef = useRef(signIn);
  const signOutRef = useRef(signOut);
  signUpRef.current = signUp;
  signInRef.current = signIn;
  signOutRef.current = signOut;

  const actions = useMemo<AuthActionsValue>(
    () => ({
      signUp: (...args) => signUpRef.current(...args),
      signIn: (...args) => signInRef.current(...args),
      signOut: () => signOutRef.current(),
    }),
    [],
  );

  const state = useMemo<AuthStateValue>(
    () => ({ user, session, loading, userStatus }),
    [user, session, loading, userStatus],
  );

  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthStateContext.Provider value={state}>
        {children}
      </AuthStateContext.Provider>
    </AuthActionsContext.Provider>
  );
}

/**
 * Hook público mantido para retrocompatibilidade com os 380+ call sites.
 * Compõe estado + ações; só use isso quando precisar de ambos.
 *
 * Para componentes que só disparam ações (ex.: botão de logout), prefira
 * `useAuthActions()` — não re-renderiza quando `user`/`session`/`loading` mudam.
 */
export function useAuth(): AuthContextType {
  const state = useContext(AuthStateContext);
  const actions = useContext(AuthActionsContext);
  if (state === undefined || actions === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return { ...state, ...actions };
}

/**
 * Acessa apenas o estado de autenticação (user/session/loading/userStatus).
 * Equivalente a `useAuth()` quando não se precisa das ações.
 */
export function useAuthState(): AuthStateValue {
  const state = useContext(AuthStateContext);
  if (state === undefined) {
    throw new Error('useAuthState must be used within an AuthProvider');
  }
  return state;
}

/**
 * Acessa apenas as ações estáveis (signIn/signUp/signOut). Consumidores
 * não re-renderizam quando o estado de auth muda — ideal para botões de
 * logout em sidebars/menus.
 */
export function useAuthActions(): AuthActionsValue {
  const actions = useContext(AuthActionsContext);
  if (actions === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return actions;
}

